<template>
  <div class="top_btn">
    <a-button
      v-if="is_form_design"
      type="primary"
      size="small"
      @click="openModelConfig"
      :ghost="true"
    >
      编辑表单
    </a-button>
    <a-button
      v-if="hasClearBtn && !isLocked"
      size="small"
      type="primary"
      @click="handleClear"
      :danger="true"
    >
      清空
    </a-button>
    <a-button
      v-if="hasDownloadBtn && !isLocked"
      size="small"
      type="primary"
      :loading="exportExcelLoading"
      @click="handleExportExcel"
    >
      下载导入模版
    </a-button>
    <a-button
      v-if="hasImportBtn && !isLocked"
      size="small"
      type="primary"
      @click="handleImportExcel"
    >
      导入Excel数据
    </a-button>
    <a-button
      v-if="
        hasAddBtn && (!field.custom_btn_list?.length || field.max_count !== min_count) && !isLocked
      "
      type="primary"
      size="small"
      @click="addFormItem"
      :disabled="field.max_count && value.length >= field.max_count"
    >
      <template #icon>
        <plus-outlined />
      </template>
      {{ field.btn_title || '添加' }}
    </a-button>
    <template v-for="btn in field.custom_btn_list">
      <debug-code
        :action_code_id="btn.action_code_id"
        v-model:code="btn.code"
        v-model:action="btn.action"
        :options="actionTypes"
      >
        <a-button
          v-if="!isLocked || btn.show_on_modify"
          size="small"
          :type="btn.type"
          :danger="btn.danger"
          @click="handleCustomBtnClick(btn)"
          :disabled="field.max_count && value.length >= field.max_count && btn.action === 'add'"
        >
          {{ btn.label }}
        </a-button>
      </debug-code>
    </template>
  </div>
  <template v-if="field.showType === 'table'">
    <FormTemplateTable
      :value="value"
      :field="field"
      :is_form_design="is_form_design"
      :alwaysShowGroup="alwaysShowGroup"
      :inner-model="innerModel"
      :global-inner-model="globalInnerModel"
      :nestValidate="nestValidate"
      :base-key-path="baseKeyPath"
      :worker-host-functions="workerHostFunctions"
      :min_count="min_count"
      :lock-structure="isLocked"
      :formModel="formModel"
      @remove="removeFormItem"
      @copy="copyFormItem"
      @labels_resolved="onLabelsResolved"
    />
  </template>
  <template v-else>
    <template v-for="(_, index) in value">
      <FormItem
        :form="field.form"
        :field="field"
        v-model:value="value[index]"
        :options="field.form?.options"
        :alwaysShowGroup="alwaysShowGroup"
        :is_form_design="is_form_design"
        :inner-model="_innerModel[index]"
        :global-inner-model="globalInnerModel"
        :modalTypeList="modalTypeList"
        :modalType="modalType"
        :nestValidate="nestValidate"
        :min_count="min_count"
        :listLength="value.length"
        :context="context"
        :lock-structure="isLocked"
        @remove="removeFormItem(index)"
        @copy="copyFormItem(index)"
        :base-key-path="[...baseKeyPath, field.name, index]"
        :worker-host-functions="workerHostFunctions"
      />
    </template>
  </template>
</template>

<script setup lang="ts">
import modalBox from 'liyu-pc-base/components/form-modal/modal-tools';
import {
  computed,
  watch,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  inject,
  unref,
  h,
  defineAsyncComponent,
  createVNode,
} from 'vue';
import mitt from 'mitt';
import { cloneDeep, debounce } from 'lodash';
import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons-vue';
import { Modal } from 'ant-design-vue';
import { exportJsonToExcelByTokenHeader } from 'liyu-pc-base/utils/excel';
import FormItem from './FormItem.vue';
import debugCode from '../debugCode.vue';
import { executeScriptInWorker } from 'liyu-pc-base/utils/function';
import { message } from 'ant-design-vue';
import FormTemplateTable from './FormTemplateTable.vue';
import {
  computeDynamicValue,
  allocateSerialBatch,
  getSerialKey,
  isSerialKeyAllocating,
  acquireSerialKeys,
  releaseSerialKeys,
} from '../DynamicValueField/computeDynamicValue';
import { normalizeFieldExtraAddonValue } from '../addonValue';

const props = defineProps<{
  field: Record<string, any>;
  modalType: string;
  value: any[];
  init_model?: Record<string, any>;
  watch: any[];
  formModel: Record<string, any>;
  is_form_design?: boolean;
  alwaysShowGroup?: boolean;
  innerModel?: Record<string, any>;
  globalInnerModel?: Record<string, any>;
  modalTypeList?: any[];
  nestValidate?: Record<string, any> | null;
  context?: Record<string, any>;
  baseKeyPath?: any[];
  workerHostFunctions?: Record<string, any>;
  contextOptions?: any[];
  // 修改模式下传入 true：隐藏系统按钮（添加/清空/导入/行删除），只保留字段值编辑
  lockStructure?: boolean;
}>();

// 兼容 prop 直传与深层 provide/inject 两种来源（默认 false，向后兼容）
const lockStructureInject = inject('FORM_LOCK_STRUCTURE', ref(false));
const isLocked = computed(() => !!props.lockStructure || !!unref(lockStructureInject));

const emit = defineEmits(['update:value']);

const height = ref(0);
const eventBus = mitt();
const actionTypes = [
  {
    label: '添加(add)',
    value: 'add',
  },
  {
    label: '整体更新(update)',
    value: 'update',
  },
];

const ImportExcelModal = defineAsyncComponent(
  () => import('liyu-pc-base/components/form-modal/ImportExcelModal/ImportExcelModal.vue'),
);

const hasDownloadBtn = computed(() => {
  if (props.field.sys_btn_list == null) {
    return true;
  }
  if (props.field.sys_btn_list.includes('download')) {
    return true;
  }
  return false;
});

const hasImportBtn = computed(() => {
  if (props.field.sys_btn_list == null) {
    return true;
  }
  if (props.field.sys_btn_list.includes('import')) {
    return true;
  }
  return false;
});

const handleClear = () => {
  Modal.confirm({
    title: '确认清空吗？',
    icon: createVNode(ExclamationCircleOutlined),
    content: '清空后将无法恢复',
    okText: '确认',
    cancelText: '取消',
    onOk: () => {
      emit('update:value', []);
    },
  });
};

const hasClearBtn = computed(() => {
  if (props.field.sys_btn_list == null) {
    return true;
  }
  if (props.field.sys_btn_list.includes('clear')) {
    return true;
  }
  return false;
});

const hasAddBtn = computed(() => {
  if (props.field.sys_btn_list == null) {
    return true;
  }
  if (props.field.sys_btn_list.includes('add')) {
    return true;
  }
  return false;
});

const name2Obj: any = {};
// remoteselectpro 字段配置映射：导入时已知 label 文本，直接回填展示字段
const remoteProFieldObj: any = {};

const handleImportExcel = () => {
  const configList: any = [];
  const dynamicFieldList = props.field.form?.dynamicFieldList || [];
  dynamicFieldList.forEach((item: any) => {
    if (item.children instanceof Array) {
      item.children.forEach((item1: any) => {
        // 忽略 is_table_hide_column 的列
        if (item1.is_table_hide_column) return;
        // 排除只读动态值（dynamicvaluefield）：流水号由后端预占用分配，不应从 Excel 导入
        if (item1.type === 'dynamicvaluefield') return;
        const col = { ...item1 };
        if (item1?.name2) {
          name2Obj[item1.name] = item1?.name2;
        }
        if (item1.type === 'remoteselectpro') {
          remoteProFieldObj[item1.name] = item1;
        }
        if (['select', 'radio', 'checkbox', 'treeselect', 'cascader'].includes(item1.type)) {
          col.options = props.field.form?.options?.[item1.name] || item1.options;
        }
        configList.push(col);
      });
    }
  });
  const tempModal = reactive({
    open: true,
    loading: false,
    component: ImportExcelModal,
    title: '导入Excel数据',
    configList: configList,
    ok: (data: any) => {
      const err: any[] = [];
      const list: any[] = [];
      data.forEach((item: any) => {
        const obj: any = cloneDeep(props.field.form.model || {});
        Object.keys(item).forEach((key: any) => {
          if (item[key].error) {
            err.push(h('li', item[key].error + '(' + item[key].excel_position + ')'));
          }
          obj[key] = item[key].value;
          if (name2Obj[key]) {
            obj[name2Obj[key]] = item[key].raw_value;
          }
          // remoteselectpro：Excel 导入时已由 label 文本匹配出 value，raw_value 即 label。
          // 直接回填 `name + '.label'` 展示字段，避免分页后非当前页行未挂载组件导致 label 缺失。
          const remoteField = remoteProFieldObj[key];
          if (remoteField) {
            const rawValue = item[key].raw_value;
            if (
              rawValue !== undefined &&
              rawValue !== null &&
              rawValue !== '' &&
              (remoteField.mode === 'default' || !remoteField.mode)
            ) {
              obj[key + '.label'] = rawValue;
            }
          }
        });
        // 导入行清除只读动态值（dynamicvaluefield）字段：
        // 由 syncDynamicValues 统一调用后端分配流水号，避免复用模板旧值导致重复
        if (dynamicFieldList?.length) {
          dynamicFieldList.forEach((group: any) => {
            if (group.children instanceof Array) {
              group.children.forEach((item1: any) => {
                if (item1.type === 'dynamicvaluefield') {
                  delete obj[item1.name];
                }
              });
            }
          });
        }
        list.push(obj);
      });
      if (err.length > 0) {
        Modal.warning({
          title: `导入Excel报错, 请改正后再尝试导入`,
          content: h('ol', err),
          icon: createVNode(ExclamationCircleOutlined),
        });
      } else {
        const newValue = [...(props.value || []), ...list];
        emit('update:value', newValue);
      }
      tempModal.open = false;
    },
    onCancel: () => {
      tempModal.open = false;
    },
  });
  modalBox(tempModal);
};

const exportExcelLoading = ref(false);

const hasReqrired = (fieldName: string) => {
  if (props.field?.form?.rules) {
    const rule = props.field?.form?.rules[fieldName];
    if (rule) {
      for (const item of rule) {
        if (item.required) {
          return true;
        }
      }
    }
  }
  return false;
};

const handleExportExcel = () => {
  exportExcelLoading.value = true;
  const header: any[] = [];
  const data: any[] = [];
  const dynamicFieldList = props.field.form?.dynamicFieldList || [];
  dynamicFieldList.forEach((item: any) => {
    if (item.children instanceof Array) {
      item.children.forEach((item1: any) => {
        if (item1.is_hide || item1.is_table_hide_column) return;
        // 排除只读动态值（dynamicvaluefield）：流水号由后端分配，不写入导入模板，避免复用旧值
        if (item1.type === 'dynamicvaluefield') return;
        header.push(
          hasReqrired(item1.name)
            ? {
                title: [item1.label, { text: '[必填]', color: 'FFFF0000' }],
              }
            : {
                title: [item1.label],
              },
        );
        if (props.value && props.value.length > 0) {
          data.push(props.value[0][item1.name] ? props.value[0][item1.name].toString() : '');
        } else {
          data.push('');
        }
      });
    }
  });

  exportJsonToExcelByTokenHeader({
    data: [data],
    autoWidth: true,
    filename: props.field.label || '导入模板',
    header: header,
  });
  setTimeout(() => {
    exportExcelLoading.value = false;
  }, 1500);
};

const min_count = computed(() => {
  return props.field.min_count ?? 0;
});

const _innerModel = computed(() => {
  if (props.innerModel) {
    if (!props.innerModel[props.field.name]) {
      props.innerModel[props.field.name] = [];
    }
    for (let i = 0; i < props.value.length; i++) {
      if (!props.innerModel[props.field.name][i]) {
        props.innerModel[props.field.name][i] = {};
      }
    }
    return props.innerModel[props.field.name];
  }
  return [];
});

const handleCustomBtnClick = async (btn: any) => {
  if (btn.code) {
    let res;
    try {
      res = await executeScriptInWorker(
        btn.code,
        {
          context: props.context,
          value: props.value,
          keyPath: [...props.baseKeyPath, props.field.name],
        },
        props.workerHostFunctions || {},
      );
    } catch (e: any) {
      message.error(e.message);
      return;
    }
    if (btn.action == 'add') {
      if (res) {
        const temp = cloneDeep(props.field.form.model || {});
        Object.assign(temp, res);
        const new_list = JSON.parse(JSON.stringify(props.value)) || [];
        new_list.push(temp);
        emit('update:value', new_list);
      }
    } else if (btn.action == 'update') {
      if (res instanceof Array) {
        const temp: any[] = [];
        for (const item of res) {
          const newItem = cloneDeep(props.field.form.model || {});
          Object.assign(newItem, item);
          temp.push(newItem);
        }
        emit('update:value', temp);
      }
    }
  }
};

const addFormItem = () => {
  const dataList = props.value || [];
  dataList.push(cloneDeep(props.field.form.model || {}));
  emit('update:value', dataList);
};

const removeFormItem = (index: number) => {
  const dataList = props.value || [];
  dataList.splice(index, 1);
  emit('update:value', dataList);
};

/**
 * 复制行：深拷贝源行数据，插入到源行的下一行（表单模式与表格模式共用）。
 *
 * 1. 流水号（serial）字段不复用旧值，删除后交由 syncDynamicValues 重新取号，
 *    避免复制出的新行与原行流水号重复（与 Excel 导入的处理保持一致）。
 * 2. innerModel / globalInnerModel 的行下标与 value 一一对应，必须同步插入副本，
 *    否则后续行的 label、选项、以及依赖 innerModel 的只读动态值会整体错位。
 */
const copyFormItem = (index: number) => {
  const dataList = props.value || [];
  const sourceRow = dataList[index];
  if (!sourceRow) return;
  if (props.field.max_count && dataList.length >= props.field.max_count) return;

  const newRow = cloneDeep(sourceRow);
  for (const dvField of dynamicValueFields.value) {
    if (dvField.watchType !== 'serial') continue;
    delete newRow[dvField.name];
  }
  dataList.splice(index + 1, 0, newRow);

  // 用 Set 去重：FormRender3 中 innerModel 未传入时会与 globalInnerModel 指向同一对象
  const innerArrays = new Set<any>();
  for (const model of [props.innerModel, props.globalInnerModel]) {
    const arr = model?.[props.field.name];
    if (arr instanceof Array) innerArrays.add(arr);
  }
  innerArrays.forEach(arr => {
    arr.splice(index + 1, 0, cloneDeep(arr[index] || {}));
  });

  emit('update:value', dataList);
};

const onresize = debounce(() => {
  height.value = window.innerHeight - 165;
}, 200);

const openModelConfig = () => {
  const tempModal = reactive({
    open: true,
    loading: false,
    component: 'InnerFormDesignModal',
    record: { value: props.field.form || {} },
    height: height.value,
    enableBatchAdd: true,
    modalTypeList: props.modalTypeList,
    modalType: props.modalType,
    eventBus: eventBus,
    contextOptions: props.contextOptions,
    ok: (data: any) => {
      props.field.form = data;
    },
    cancel: () => {
      console.log('cancel');
    },
  });
  modalBox(tempModal);
};

watch(
  () => props.value,
  newValue => {
    if (!(newValue instanceof Array) || newValue.length < min_count.value) {
      const res = [...(newValue || [])];
      while (res.length < min_count.value) {
        res.push(cloneDeep(props.field.form.model || {}));
      }
      emit('update:value', res);
    }
  },
);

/**
 * 从 dynamicFieldList 中提取所有 type 为 dynamicvaluefield 的字段配置，
 * 参考 FormTemplateTable.vue 中 genTableColumns 的遍历逻辑。
 */
const dynamicValueFields = computed(() => {
  const result: any[] = [];
  const dynamicFieldList = props.field?.form?.dynamicFieldList || [];
  dynamicFieldList.forEach((group: any) => {
    if (group.children instanceof Array) {
      group.children.forEach((item: any) => {
        if (item.type === 'dynamicvaluefield') {
          result.push(item);
        }
      });
    }
  });
  return result;
});

const addonValueFields = computed(() => {
  const result: any[] = [];
  const dynamicFieldList = props.field?.form?.dynamicFieldList || [];
  dynamicFieldList.forEach((group: any) => {
    if (group.children instanceof Array) {
      group.children.forEach((item: any) => {
        if (
          (item.extraAddonBefore && item.addonBefore) ||
          (item.extraAddonAfter && item.addonAfter)
        ) {
          result.push(item);
        }
      });
    }
  });
  return result;
});

const isSyncDynamicValuesAllocating = ref(false);

/**
 * 表格模式 + 存在 remoteselectpro 时，非当前页行不会挂载组件，
 * innerModel/globalInnerModel 由 FormTemplateTable 的 label 回填异步生成，
 * 只读动态值（dynamicvaluefield）依赖这些值，因此必须等回填完成后再计算。
 */
const hasTableRemoteSelectPro = computed(() => {
  if (props.field?.showType !== 'table' || props.is_form_design) return false;
  const dynamicFieldList = props.field?.form?.dynamicFieldList || [];
  return dynamicFieldList.some((g: any) => {
    return (
      g.children instanceof Array &&
      g.children.some(
        (c: any) => c.type === 'remoteselectpro' && c.postUrl && !c.is_table_hide_column,
      )
    );
  });
});

/** 表格 label/innerModel 回填进行中标记：置为 true 时跳过只读动态值计算 */
const pendingTableLabelBackfill = ref(false);

/** 表格回填完成事件：解除等待并重新计算只读动态值 */
const onLabelsResolved = () => {
  pendingTableLabelBackfill.value = false;
  syncDynamicValues();
  syncAddonValues();
};

/**
 * 遍历 value 每条数据，应用 dynamicvaluefield 的计算逻辑，
 * 将计算结果写入对应 name 的 key。
 *
 * 其中 formModel 对应 value 数组的每一条数据（行数据），
 * innerModel 对应该行的 innerModel，
 * baseKeyPath 为 [...baseKeyPath, field.name, rowIndex]。
 */
const syncDynamicValues = debounce(async () => {
  // ★ 防重入：分配请求未完成时跳过本轮触发，避免同一行重复分配号码
  if (isSyncDynamicValuesAllocating.value) return;
  // ★ 等待表格 remoteselectpro 的 innerModel/globalInnerModel 回填完成后再计算，
  //   否则 scoped/global 类型会因 innerModel 为空而把动态值清成 ''
  if (pendingTableLabelBackfill.value) return;
  isSyncDynamicValuesAllocating.value = true;
  try {
    const valueList = props.value;
    if (!valueList || valueList.length === 0) return;
    if (dynamicValueFields.value.length === 0) return;

    let hasChanges = false;
    const newValue = [...valueList];

    // ★ serial 字段批量分配：按字段收集空值行，一次请求批量取号（M1-M5 通用），
    //   替代逐行 N 次请求，消除「导入量大、生成未完成即保存，覆盖ID为空」的竞态
    for (const dvField of dynamicValueFields.value) {
      if (dvField.watchType !== 'serial') continue;
      // 修改模式（结构锁定）下 M5 覆盖ID禁止重新分配（与 computeDynamicValue 守卫-锁一致）
      if (isLocked.value && dvField.serialConfig?.mode === 'M5') continue;
      // 收集空值行（守卫1：已有值不重复分配）
      const pendingRows: number[] = [];
      for (let i = 0; i < newValue.length; i++) {
        const currentValue = newValue[i]?.[dvField.name];
        if (currentValue === undefined || currentValue === null || currentValue === '') {
          pendingRows.push(i);
        }
      }
      if (pendingRows.length === 0) continue;
      if (props.is_form_design) {
        // 设计/编辑模式：预览示例不占用号码，同一字段所有空值行展示同一示例（一次请求）
        const result = await computeDynamicValue({
          field: dvField,
          currentValue: undefined,
          innerModel: null,
          globalInnerModel: props.globalInnerModel,
          formModel: null,
          context: props.context,
          baseKeyPath: [...(props.baseKeyPath || []), props.field.name],
          workerHostFunctions: props.workerHostFunctions || {},
          is_form_design: true,
          lock_structure: isLocked.value,
        });
        if (result && result.value !== undefined && result.value !== null) {
          for (const i of pendingRows) {
            newValue[i] = { ...newValue[i], [dvField.name]: result.value };
            hasChanges = true;
          }
        }
        continue;
      }
      // 与单行分配互斥：被 DynamicValueField 单行请求占用的行跳过，由其自行完成
      const baseKeyPath = [...(props.baseKeyPath || []), props.field.name];
      const candidates = pendingRows
        .map(i => ({ row: i, key: getSerialKey(dvField, [...baseKeyPath, i]) }))
        .filter(c => !isSerialKeyAllocating(c.key));
      if (candidates.length === 0) continue;
      const activeKeys = candidates.map(c => c.key);
      if (!acquireSerialKeys(activeKeys)) continue;
      try {
        const { codes, error } = await allocateSerialBatch(dvField, candidates.length);
        if (error || !codes) {
          // 失败：拦截器已自动弹错误提示，本轮跳过，待下次触发重试
          console.error('流水号批量分配失败:', error);
        } else {
          candidates.forEach((c, k) => {
            newValue[c.row] = { ...newValue[c.row], [dvField.name]: codes[k] };
            hasChanges = true;
          });
        }
      } finally {
        releaseSerialKeys(activeKeys);
      }
    }

    // 非 serial 字段逐行计算（纯前端计算/worker，无后端取号请求）
    for (let i = 0; i < newValue.length; i++) {
      const rowInnerModel = _innerModel.value[i];
      const rowBaseKeyPath = [...(props.baseKeyPath || []), props.field.name, i];

      for (const dvField of dynamicValueFields.value) {
        if (dvField.watchType === 'serial') continue;
        const currentValue = newValue[i]?.[dvField.name];
        const result = await computeDynamicValue({
          field: dvField,
          currentValue,
          innerModel: rowInnerModel,
          globalInnerModel: props.globalInnerModel,
          formModel: newValue[i],
          context: props.context,
          baseKeyPath: rowBaseKeyPath,
          workerHostFunctions: props.workerHostFunctions || {},
          is_form_design: props.is_form_design,
          lock_structure: isLocked.value,
        });
        if (result) {
          // 空值不回填（serial 失败时避免触发更新循环）
          if (result.value !== undefined && result.value !== null) {
            newValue[i] = { ...newValue[i], [dvField.name]: result.value };
            hasChanges = true;
          }
        }
      }
    }

    if (hasChanges) {
      emit('update:value', newValue);
    }
  } finally {
    isSyncDynamicValuesAllocating.value = false;
  }
}, 200);

const syncAddonValues = debounce(() => {
  const valueList = props.value;
  if (!valueList || valueList.length === 0) return;
  if (addonValueFields.value.length === 0) return;

  let hasChanges = false;
  const newValue = [...valueList];

  for (let i = 0; i < newValue.length; i++) {
    const row = newValue[i];
    if (!row || typeof row !== 'object') continue;

    let nextRow = row;
    for (const addonField of addonValueFields.value) {
      const currentValue = nextRow[addonField.name];
      const normalizedValue = normalizeFieldExtraAddonValue(addonField, currentValue);
      if (normalizedValue !== currentValue) {
        if (nextRow === row) {
          nextRow = { ...row };
        }
        nextRow[addonField.name] = normalizedValue;
        hasChanges = true;
      }
    }
    newValue[i] = nextRow;
  }

  if (hasChanges) {
    emit('update:value', newValue);
  }
}, 200);

watch(
  () => [props.value, props.context, props.field],
  () => {
    // 这些变化会触发 FormTemplateTable 的 label 回填，先标记等待，
    // 待 @labels_resolved 后再计算只读动态值
    if (hasTableRemoteSelectPro.value) {
      pendingTableLabelBackfill.value = true;
    } else {
      // showType 切走等场景：不再有回填，及时解除等待避免卡死
      pendingTableLabelBackfill.value = false;
    }
    syncDynamicValues();
    syncAddonValues();
  },
  { deep: true, immediate: true },
);
// innerModel/globalInnerModel 变化（含回填写入）：直接尝试计算，
// 回填进行中时 syncDynamicValues 内部的 pending 标记会拦住
watch(
  () => [props.innerModel, props.globalInnerModel],
  () => {
    syncDynamicValues();
    syncAddonValues();
  },
  { deep: true },
);

onMounted(() => {
  window.addEventListener('resize', onresize);
  onresize();
  if (!(props.value instanceof Array) || props.value.length < min_count.value) {
    const res = [...(props.value || [])];
    while (res.length < min_count.value) {
      res.push(cloneDeep(props.field.form.model || {}));
    }
    emit('update:value', res);
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', onresize);
  onresize.cancel();
  syncDynamicValues.cancel();
  syncAddonValues.cancel();
});
</script>

<style lang="less" scoped>
.top_btn {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}
.form-wrap {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 4px;
  margin-bottom: 4px;
  width: 100%;
  .form-content {
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }
  .form-action {
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 8px;
    flex-shrink: 0;
  }
}
</style>
